import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// One colour per MEANING, shared by every status pill in the app (travel
// requests, advances, approval records, the Approver Queue) - so the same
// status reads the same colour on every page. Previously each page kept
// its own copy of a status → variant map and they had drifted (an Approved
// advance was uncoloured in My Reimbursements but solid green in the
// Approver Queue; "Needs changes" looked identical to Rejected). The hues
// are the theme tokens the approval timeline and dashboard charts also use.
export type StatusTone =
  | "neutral" // Draft - not in anyone's queue yet
  | "pending" // waiting on an approver (Manager or Finance)
  | "approved" // signed off, still moving (e.g. awaiting payment)
  | "settled" // finished successfully - Paid / Completed
  | "rejected"
  | "changes"; // sent back to the employee to fix (Resubmit)

const TONE_CLASS: Record<StatusTone, string> = {
  neutral: "",
  pending: "border-transparent bg-[hsl(var(--quadra-orange))] text-black",
  approved: "border-transparent bg-primary text-primary-foreground",
  settled: "border-primary bg-primary/10 text-primary",
  rejected: "border-transparent bg-destructive text-destructive-foreground",
  changes:
    "border-[hsl(var(--quadra-orange))] bg-[hsl(var(--quadra-orange)/0.1)] text-amber-700 dark:text-amber-400"
};

// Every status value used anywhere (TravelRequestStatus, AdvanceStatus,
// ApprovalStatus, plus the Approver Queue's derived "Completed"). Unknown
// values fall back to neutral rather than throwing.
const STATUS_TONE: Record<string, StatusTone> = {
  Draft: "neutral",
  Submitted: "pending",
  PendingFinance: "pending",
  Pending: "pending",
  Approved: "approved",
  Completed: "settled",
  Paid: "settled",
  Rejected: "rejected",
  ResubmitRequested: "changes",
  Resubmit: "changes"
};

export function statusTone(status: string): StatusTone {
  return STATUS_TONE[status] ?? "neutral";
}

export function StatusBadge({
  status,
  label,
  className
}: {
  status: string;
  /** Text shown - defaults to the raw status. Pages pass their own wording
   *  ("Needs changes", "Awaiting payment", …); the colour still comes from
   *  `status`, so a relabelled status keeps its standard colour. */
  label?: string;
  className?: string;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "whitespace-nowrap font-normal",
        TONE_CLASS[statusTone(status)],
        className
      )}
    >
      {label ?? status}
    </Badge>
  );
}
